import {
  Spinner,
  Button,
  Badge,
  Card,
  Text,
  tokens,
  Divider,
  ProgressBar,
} from '@fluentui/react-components';
import {
  DocumentPdfRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  LinkRegular,
} from '@fluentui/react-icons';
import type { DrakeMatchStatus } from '../../types';
import { matchStatusDisplayNames, matchStatusColors } from './types';
import type { UploadTabProps } from './types';

export default function UploadTab({
  isBatchMode,
  setIsBatchMode,
  uploading,
  activeJob,
  jobPolling,
  setActiveJob,
  clearJob,
  setSelectedTab,
  handleFileUpload,
  handleDrop,
  handleDragOver,
  fileInputRef,
  batchFileInputRef,
  batchUploading,
  activeBatch,
  batchResponse,
  handleBatchUpload,
  clearBatch,
  uploadResult,
  setUploadResult,
  uploadError,
  setSelectedChecklist,
  setDetailDialogOpen,
  setMatchDialogOpen,
}: UploadTabProps) {
  return (
    <div>
      {/* Upload Mode Toggle */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
        <Button
          appearance={!isBatchMode ? 'primary' : 'secondary'}
          onClick={() => setIsBatchMode(false)}
        >
          Single Upload
        </Button>
        <Button
          appearance={isBatchMode ? 'primary' : 'secondary'}
          onClick={() => setIsBatchMode(true)}
        >
          Bulk Upload (up to 50 files)
        </Button>
      </div>

      {/* Single Upload Area */}
      {!isBatchMode && (
        <Card
          style={{
            padding: '48px',
            textAlign: 'center',
            border: `2px dashed ${activeJob || jobPolling ? tokens.colorBrandStroke1 : tokens.colorNeutralStroke1}`,
            backgroundColor: activeJob || jobPolling ? tokens.colorBrandBackground2 : tokens.colorNeutralBackground2,
            cursor: uploading || activeJob ? 'default' : 'pointer',
          }}
          onDrop={!uploading && !activeJob ? handleDrop : undefined}
          onDragOver={!uploading && !activeJob ? handleDragOver : undefined}
          onClick={() => !uploading && !activeJob && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            style={{ display: 'none' }}
            onChange={(e) => handleFileUpload(e.target.files)}
          />

          {uploading ? (
            <div>
              <Spinner size="large" />
              <Text as="p" style={{ marginTop: '16px' }}>Uploading PDF...</Text>
            </div>
          ) : activeJob || jobPolling ? (
            <div style={{ maxWidth: '500px', margin: '0 auto' }}>
              <Text as="p" size={500} weight="semibold" style={{ marginBottom: '16px' }}>
                {activeJob?.status === 'completed' ? 'Processing Complete!' :
                 activeJob?.status === 'failed' ? 'Processing Failed' :
                 'Processing PDF in Background...'}
              </Text>
              {activeJob?.originalFileName && (
                <Text as="p" style={{ color: tokens.colorNeutralForeground3, marginBottom: '16px' }}>
                  {activeJob.originalFileName}
                </Text>
              )}
              {activeJob && activeJob.status !== 'failed' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <Text>Progress</Text>
                    <Text weight="semibold">
                      {activeJob.checklistsCreated} / {activeJob.totalClientsFound || '?'} clients
                    </Text>
                  </div>
                  <ProgressBar
                    value={activeJob.totalClientsFound > 0 ? activeJob.checklistsCreated / activeJob.totalClientsFound : 0}
                    style={{ marginBottom: '16px' }}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', textAlign: 'center' }}>
                    <div>
                      <Text size={600} weight="bold" style={{ color: tokens.colorBrandForeground1 }}>
                        {activeJob.totalClientsFound || 0}
                      </Text>
                      <Text as="p" size={200}>Clients Found</Text>
                    </div>
                    <div>
                      <Text size={600} weight="bold" style={{ color: tokens.colorPaletteGreenForeground1 }}>
                        {activeJob.checklistsCreated}
                      </Text>
                      <Text as="p" size={200}>Created</Text>
                    </div>
                    <div>
                      <Text size={600} weight="bold" style={{ color: tokens.colorPaletteMarigoldForeground1 }}>
                        {activeJob.checklistsMatched}
                      </Text>
                      <Text as="p" size={200}>Matched</Text>
                    </div>
                  </div>
                  <Text as="p" size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: '16px' }}>
                    {activeJob.status === 'completed' ?
                      'All clients processed successfully!' :
                      'You can safely leave this page. Progress is saved automatically.'}
                  </Text>
                  {activeJob.status === 'processing' && (
                    <Button
                      appearance="subtle"
                      style={{ marginTop: '16px' }}
                      onClick={clearJob}
                    >
                      Dismiss
                    </Button>
                  )}
                </div>
              )}
              {activeJob?.status === 'failed' && (
                <div style={{ color: tokens.colorPaletteRedForeground1 }}>
                  <DismissCircleRegular style={{ fontSize: tokens.fontSizeHero800 }} />
                  <Text as="p" style={{ marginTop: '8px' }}>
                    {activeJob.error || 'An error occurred during processing'}
                  </Text>
                  {activeJob.perClientErrors && activeJob.perClientErrors.length > 0 && (
                    <div style={{ marginTop: '12px', textAlign: 'left' }}>
                      <Text weight="semibold" size={200} style={{ display: 'block', marginBottom: '8px' }}>
                        Failed clients:
                      </Text>
                      {activeJob.perClientErrors.map((e, i) => (
                        <Text key={i} as="p" size={200} style={{ marginBottom: '4px' }}>
                          {e.clientName}: {e.error}{e.extractionTier ? ` (${e.extractionTier})` : ''}
                        </Text>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {activeJob?.status === 'completed' && (
                <div style={{ marginTop: '24px' }}>
                  <CheckmarkCircleRegular style={{ fontSize: tokens.fontSizeHero800, color: tokens.colorPaletteGreenForeground1 }} />
                  <Button
                    appearance="primary"
                    style={{ marginTop: '16px' }}
                    onClick={() => {
                      setActiveJob(null);
                      setSelectedTab('checklists');
                    }}
                  >
                    View Checklists ({activeJob.checklistsCreated})
                  </Button>
                </div>
              )}
              {(activeJob?.status === 'failed' || activeJob?.status === 'completed') && (
                <Button
                  appearance="subtle"
                  style={{ marginTop: '16px' }}
                  onClick={() => setActiveJob(null)}
                >
                  Upload Another
                </Button>
              )}
            </div>
          ) : (
            <div>
              <DocumentPdfRegular style={{ fontSize: '64px', color: tokens.colorBrandForeground1 }} />
              <Text as="p" size={500} style={{ marginTop: '16px' }}>
                Drag and drop a Drake Proforma PDF here
              </Text>
              <Text as="p" style={{ color: tokens.colorNeutralForeground3, marginTop: '8px' }}>
                or click to browse files
              </Text>
              <Button appearance="primary" style={{ marginTop: '16px' }}>
                Select PDF File
              </Button>
              <Text as="p" size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: '16px' }}>
                Supports multi-client PDFs. Processing happens in the background - you can leave and check back.
              </Text>
            </div>
          )}
        </Card>
      )}

      {/* Bulk Upload Area */}
      {isBatchMode && (
        <Card
          style={{
            padding: '48px',
            textAlign: 'center',
            border: `2px dashed ${tokens.colorBrandStroke1}`,
            backgroundColor: tokens.colorBrandBackground2,
            cursor: batchUploading ? 'default' : 'pointer',
          }}
          onClick={() => !batchUploading && batchFileInputRef.current?.click()}
        >
          <input
            ref={batchFileInputRef}
            type="file"
            accept=".pdf"
            style={{ display: 'none' }}
            onChange={(e) => handleBatchUpload(e.target.files)}
            multiple
          />

          {batchUploading || activeBatch ? (
            <div style={{ maxWidth: '500px', margin: '0 auto' }}>
              <Text as="p" size={500} weight="semibold" style={{ marginBottom: '16px' }}>
                Batch Processing in Progress
              </Text>
              {batchResponse && (
                <Text as="p" style={{ color: tokens.colorNeutralForeground3, marginBottom: '16px' }}>
                  {batchResponse.message}
                </Text>
              )}
              {activeBatch && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <Text>Progress</Text>
                    <Text weight="semibold">
                      {activeBatch.processed} / {activeBatch.totalFiles} files
                    </Text>
                  </div>
                  <ProgressBar
                    value={activeBatch.totalFiles > 0 ? activeBatch.processed / activeBatch.totalFiles : 0}
                    style={{ marginBottom: '16px' }}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', textAlign: 'center' }}>
                    <div>
                      <Text size={600} weight="bold" style={{ color: tokens.colorPaletteGreenForeground1 }}>
                        {activeBatch.matched}
                      </Text>
                      <Text as="p" size={200}>Matched</Text>
                    </div>
                    <div>
                      <Text size={600} weight="bold" style={{ color: tokens.colorPaletteMarigoldForeground1 }}>
                        {activeBatch.pendingReview}
                      </Text>
                      <Text as="p" size={200}>Pending Review</Text>
                    </div>
                    <div>
                      <Text size={600} weight="bold" style={{ color: tokens.colorPaletteRedForeground1 }}>
                        {activeBatch.failed}
                      </Text>
                      <Text as="p" size={200}>Failed</Text>
                    </div>
                    <div>
                      <Text size={600} weight="bold">
                        {activeBatch.totalFiles - activeBatch.processed}
                      </Text>
                      <Text as="p" size={200}>Remaining</Text>
                    </div>
                  </div>
                  {activeBatch.status === 'completed' && (
                    <div style={{ marginTop: '24px' }}>
                      <CheckmarkCircleRegular style={{ fontSize: tokens.fontSizeHero800, color: tokens.colorPaletteGreenForeground1 }} />
                      <Text as="p" style={{ marginTop: '8px', color: tokens.colorPaletteGreenForeground1 }}>
                        Batch processing complete!
                      </Text>
                      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '16px' }}>
                        <Button
                          appearance="primary"
                          onClick={() => {
                            clearBatch();
                            setSelectedTab('checklists');
                          }}
                        >
                          View Checklists
                        </Button>
                        <Button
                          appearance="outline"
                          onClick={clearBatch}
                        >
                          Start New Upload
                        </Button>
                      </div>
                    </div>
                  )}
                  {activeBatch.status === 'failed' && (
                    <div style={{ marginTop: '24px' }}>
                      <DismissCircleRegular style={{ fontSize: tokens.fontSizeHero800, color: tokens.colorPaletteRedForeground1 }} />
                      <Text as="p" style={{ marginTop: '8px', color: tokens.colorPaletteRedForeground1 }}>
                        Batch processing failed.
                      </Text>
                      <Button
                        appearance="outline"
                        style={{ marginTop: '16px' }}
                        onClick={clearBatch}
                      >
                        Start New Upload
                      </Button>
                    </div>
                  )}
                  {activeBatch.status !== 'completed' && activeBatch.status !== 'failed' && (
                    <Button
                      appearance="subtle"
                      style={{ marginTop: '16px' }}
                      onClick={clearBatch}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              )}
              {!activeBatch && (
                <div>
                  <Spinner size="large" />
                  <Text as="p" style={{ marginTop: '16px' }}>Starting batch upload...</Text>
                </div>
              )}
            </div>
          ) : (
            <div>
              <DocumentPdfRegular style={{ fontSize: '64px', color: tokens.colorBrandForeground1 }} />
              <Text as="p" size={500} style={{ marginTop: '16px' }}>
                Select multiple Drake Proforma PDFs for bulk processing
              </Text>
              <Text as="p" style={{ color: tokens.colorNeutralForeground3, marginTop: '8px' }}>
                Up to 50 files per batch. Processing takes ~5 seconds per file due to rate limiting.
              </Text>
              <Button appearance="primary" style={{ marginTop: '16px' }}>
                Select PDF Files (Bulk)
              </Button>
              <Text as="p" size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: '16px' }}>
                For ~2000 PDFs, upload in batches of 50. Each batch takes ~4-5 minutes to process.
              </Text>
            </div>
          )}
        </Card>
      )}

      {/* Upload Result */}
      {uploadResult && (
        <Card style={{ marginTop: '24px', padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <CheckmarkCircleRegular style={{ fontSize: tokens.fontSizeBase600, color: tokens.colorPaletteGreenForeground1 }} />
            <Text size={500} weight="semibold">Extraction Complete</Text>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div>
              <Text weight="semibold">Client Name</Text>
              <Text as="p">{uploadResult.extractedClientInfo?.name || 'Unknown'}</Text>
              {uploadResult.extractedClientInfo?.spouseName && (
                <Text as="p" style={{ color: tokens.colorNeutralForeground3 }}>
                  Spouse: {uploadResult.extractedClientInfo.spouseName}
                </Text>
              )}
            </div>
            <div>
              <Text weight="semibold">Match Status</Text>
              <Badge
                appearance="filled"
                style={{ backgroundColor: matchStatusColors[uploadResult.matchStatus as DrakeMatchStatus], marginTop: '4px' }}
              >
                {matchStatusDisplayNames[uploadResult.matchStatus as DrakeMatchStatus]}
              </Badge>
            </div>
            <div>
              <Text weight="semibold">Checklist Items</Text>
              <Text as="p">{uploadResult.checklistItems?.length || 0} documents needed</Text>
            </div>
          </div>

          {uploadResult.checklistItems && uploadResult.checklistItems.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <Divider />
              <Text weight="semibold" style={{ display: 'block', marginTop: '16px', marginBottom: '12px' }}>
                Extracted Checklist Items
              </Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {uploadResult.checklistItems.map((item, i) => (
                  <Badge key={i} appearance="outline">
                    {item.category}{item.payerName ? ` - ${item.payerName}` : ''}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
            <Button
              appearance="primary"
              onClick={() => {
                setSelectedChecklist(uploadResult);
                setDetailDialogOpen(true);
              }}
            >
              View Details
            </Button>
            {uploadResult.matchStatus !== 'matched' && (
              <Button
                appearance="secondary"
                icon={<LinkRegular />}
                onClick={() => {
                  setSelectedChecklist(uploadResult);
                  setMatchDialogOpen(true);
                }}
              >
                Match to Client
              </Button>
            )}
            <Button
              appearance="subtle"
              onClick={() => {
                setUploadResult(null);
              }}
            >
              Upload Another
            </Button>
          </div>
        </Card>
      )}

      {/* Upload Error */}
      {uploadError && (
        <Card style={{ marginTop: '24px', padding: '24px', backgroundColor: tokens.colorPaletteRedBackground1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <DismissCircleRegular style={{ fontSize: tokens.fontSizeBase600, color: tokens.colorPaletteRedForeground1 }} />
            <div>
              <Text weight="semibold">Upload Failed</Text>
              <Text as="p">{uploadError}</Text>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
